import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ProductImageLoupe as ProductMediaGallery } from "@/components/product/ProductImageLoupe";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { usd, ago, num } from "@/lib/format";
import { normalizePurchaseActivityBadge } from "@/lib/currentRangeCompetition";
import { Panel } from "@/components/common/primitives";
import {
  buildWalmartProductUrl,
} from "@/lib/analyzerData";
import { normalizeProductDescription } from "@/lib/productDescription";
import {
  ProductSourceIconBar,
  buildTitleSourceLinkActions,
} from "@/components/product/ProductSourceIconBar";
import {
  Package, PanelRightOpen, PanelRightClose, X,
} from "lucide-react";
import { VerdictBadge } from "@/components/common/primitives";

const NAVY = "#102a43";
const BLUE = "#1266dc";
const LINE = "#dfe7f0";
const MUTED = "#64748b";
export const DESCRIPTION_POPOVER_ID = "product-description-popover";
export const CATALOG_POPOVER_ID = "catalog-sizes-popover";
const SIZE_DETAIL_LABEL_RE = /^(size|pack|count|quantity|net\s*weight|unit\s*size|product\s*size|volume)$/i;

/** Sizes from Oxylabs variations; fall back to Size/Pack rows in product_details. */
export function collectCatalogSizes({ variations = [], productDetails = [], currentItemId = null } = {}) {
  const sizes = [];
  const seen = new Set();
  for (const v of Array.isArray(variations) ? variations : []) {
    if (!v || typeof v !== "object") continue;
    const pack = v.pack != null && v.pack !== "" ? `${v.pack} ct` : "";
    const variant = String(v.variant || "").trim();
    const label = [variant, pack].filter(Boolean).join(" · ") || "";
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const itemId = v.item_id != null ? String(v.item_id) : null;
    sizes.push({
      id: itemId || key,
      label,
      price: Number.isFinite(Number(v.price)) ? Number(v.price) : null,
      url: typeof v.url === "string" && v.url.trim() ? v.url.trim() : null,
      itemId,
      inStock: v.in_stock,
      current: Boolean(currentItemId && itemId && itemId === String(currentItemId)),
    });
  }
  if (sizes.length > 0) return sizes;

  for (const row of Array.isArray(productDetails) ? productDetails : []) {
    if (!row || typeof row !== "object") continue;
    const name = String(row.label || "").trim();
    if (!SIZE_DETAIL_LABEL_RE.test(name)) continue;
    const value = String(row.value ?? "").trim();
    if (!value) continue;
    const label = `${name}: ${value}`;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    sizes.push({
      id: key,
      label,
      price: null,
      url: null,
      itemId: null,
      inStock: null,
      current: false,
    });
  }
  return sizes;
}

/** Strip user-facing "fixture" labels; never show "(fixture)" in the UI. */
function stripFixture(value) {
  if (value == null || value === "") return "";
  return String(value).replace(/\s*\(fixture\)\s*/gi, " ").replace(/\s+/g, " ").trim();
}

/** Product gallery images — strings or {url}; protocol-relative; drop SVG/icons; dedupe. */
function normalizeImages(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const out = [];
  const seen = new Set();
  for (const entry of raw) {
    let url = typeof entry === "string"
      ? entry.trim()
      : (entry && typeof entry === "object" ? String(entry.url || entry.imageUrl || entry.src || "").trim() : "");
    if (!url) continue;
    if (url.startsWith("//")) url = `https:${url}`;
    const low = url.toLowerCase();
    if (low.endsWith(".svg") || low.includes(".svg?")) continue;
    if (/(^|\/)(icon|sprite|logo|favicon)([/?._-]|$)/i.test(low)) continue;
    // Upgrade Walmart SEO size params when present
    try {
      const u = new URL(url);
      if (u.searchParams.has("odnHeight") || u.searchParams.has("odnWidth")) {
        u.searchParams.set("odnHeight", "640");
        u.searchParams.set("odnWidth", "640");
        url = u.toString();
      }
      const base = `${u.origin}${u.pathname}`.toLowerCase().replace(/_\d+x\d+(?=\.(?:jpe?g|png|webp))/i, "");
      if (seen.has(base)) continue;
      seen.add(base);
    } catch {
      if (seen.has(url)) continue;
      seen.add(url);
    }
    out.push(url);
  }
  return out;
}

function availabilityDisplay(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const up = s.toUpperCase().replace(/\s+/g, "_");
  if (up === "IN_STOCK" || /^in[\s_-]*stock$/i.test(s)) return { label: "In stock", inStock: true };
  if (up === "OUT_OF_STOCK" || /out[\s_-]*of[\s_-]*stock/i.test(s)) return { label: "Out of stock", inStock: false };
  if (up === "UNKNOWN") return { label: "Unknown", inStock: null };
  return { label: s, inStock: /in\s*stock/i.test(s) && !/out\s*of\s*stock/i.test(s) };
}

function IdChip({ label, value, copyTitle }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const display = value || "—";
  const canCopy = Boolean(value);
  const onCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch {
      /* ignore */
    }
    setCopied(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setCopied(false);
      timer.current = null;
    }, 900);
  };
  return (
    <div
      className="inline-flex items-center gap-1.5 sm:gap-2 border rounded-[9px] pl-2 pr-1 py-1 sm:pl-2.5 sm:pr-1.5 sm:py-1.5 bg-[#fbfdff] whitespace-nowrap max-w-full min-w-0"
      style={{ borderColor: LINE }}
    >
      <label className="text-[9px] sm:text-[10px] uppercase tracking-[0.08em] font-extrabold shrink-0" style={{ color: MUTED }}>
        {label}
      </label>
      <code className="text-[12px] sm:text-[14px] font-bold font-mono truncate max-w-[9.5rem] sm:max-w-none" style={{ color: "#142f4c" }} title={display}>
        {display}
      </code>
      <button
        type="button"
        className="shrink-0 w-[26px] h-[26px] sm:w-[27px] sm:h-[27px] rounded-lg border text-[13px] font-extrabold grid place-items-center bg-white hover:border-[#1266dc] hover:text-[#1266dc] disabled:opacity-40"
        style={{ borderColor: LINE, color: NAVY }}
        title={copyTitle}
        aria-label={copyTitle}
        disabled={!canCopy}
        onClick={onCopy}
      >
        {copied ? "✓" : "⧉"}
      </button>
    </div>
  );
}

export function ProductDescriptionPopover({
  open,
  onClose,
  anchorRef,
  description,
  features,
}) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor || typeof window === "undefined") return;
    const rect = anchor.getBoundingClientRect();
    const margin = 16;
    const width = Math.min(460, window.innerWidth - margin * 2);
    const gap = 12;
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    let top = rect.bottom + gap;
    const approxHeight = Math.min(420, window.innerHeight - 48);
    if (top + approxHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - gap - Math.min(approxHeight, rect.top - margin));
    }
    const caretLeft = Math.max(16, Math.min(width - 30, rect.left + rect.width / 2 - left - 7));
    setCoords({ top, left, width, caretLeft });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose({ returnFocus: true });
      }
    };
    const onPointer = (e) => {
      const t = e.target;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains?.(t)) return;
      onClose({ returnFocus: false });
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      id={DESCRIPTION_POPOVER_ID}
      role="dialog"
      aria-modal="false"
      aria-label="Product description"
      data-testid="product-description-popover"
      className="product-description-popover"
      style={{
        top: coords.top,
        left: coords.left,
        width: coords.width,
        ["--popover-caret-left"]: `${coords.caretLeft}px`,
      }}
    >
      <div className="relative z-[1]">
        <h3 className="m-0 mb-3 text-[13px] font-bold tracking-wide uppercase" style={{ color: NAVY }}>
          Product description
        </h3>
        <div className="product-description-popover__body ez-scroll" data-testid="product-description-body">
          {description ? (
            <p className="m-0 mb-3 text-[13px] sm:text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "#17233a" }}>
              {description}
            </p>
          ) : null}
          {features.length > 0 ? (
            <ul className="m-0 pl-4 space-y-1.5 text-[13px] sm:text-[14px] leading-snug" style={{ color: "#17233a" }} data-testid="product-description-features">
              {features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          ) : null}
          {!description && features.length === 0 ? (
            <p className="m-0 text-[13px]" style={{ color: MUTED }}>No description available.</p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function CatalogSizesPopover({ open, onClose, anchorRef, sizes }) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor || typeof window === "undefined") return;
    const rect = anchor.getBoundingClientRect();
    const margin = 16;
    const width = Math.min(320, window.innerWidth - margin * 2);
    const gap = 12;
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    let top = rect.bottom + gap;
    const approxHeight = Math.min(360, window.innerHeight - 48);
    if (top + approxHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - gap - Math.min(approxHeight, rect.top - margin));
    }
    const caretLeft = Math.max(16, Math.min(width - 30, rect.left + rect.width / 2 - left - 7));
    setCoords({ top, left, width, caretLeft });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose({ returnFocus: true });
      }
    };
    const onPointer = (e) => {
      const t = e.target;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains?.(t)) return;
      onClose({ returnFocus: false });
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      id={CATALOG_POPOVER_ID}
      role="dialog"
      aria-modal="false"
      aria-label="Product sizes"
      data-testid="catalog-sizes-popover"
      className="product-description-popover"
      style={{
        top: coords.top,
        left: coords.left,
        width: coords.width,
        ["--popover-caret-left"]: `${coords.caretLeft}px`,
      }}
    >
      <div className="relative z-[1]">
        <div className="flex items-baseline justify-between gap-2 mb-3">
          <h3 className="m-0 text-[13px] font-bold tracking-wide uppercase" style={{ color: NAVY }}>
            Product sizes
          </h3>
          <span className="text-[10px] font-semibold tracking-wide uppercase" style={{ color: MUTED }}>
            Catalog
          </span>
        </div>
        <div className="product-description-popover__body ez-scroll" data-testid="catalog-sizes-body">
          {sizes.length === 0 ? (
            <p className="m-0 text-[13px]" style={{ color: MUTED }}>No sizes available.</p>
          ) : (
            <ul className="m-0 p-0 list-none space-y-1.5" data-testid="catalog-sizes-list">
              {sizes.map((size) => {
                const stockLabel = size.inStock === true
                  ? "In stock"
                  : size.inStock === false
                    ? "Out of stock"
                    : null;
                const rowClass = cn(
                  "flex items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-[13px] leading-snug transition-colors",
                  size.current ? "bg-white/55" : "bg-white/25 hover:bg-white/45",
                );
                const content = (
                  <>
                    <span className="min-w-0 flex flex-col gap-0.5">
                      <span className="font-semibold truncate" style={{ color: NAVY }}>
                        {size.label}
                        {size.current ? (
                          <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: BLUE }}>
                            Current
                          </span>
                        ) : null}
                      </span>
                      {stockLabel ? (
                        <span className="text-[11px] font-medium" style={{ color: MUTED }}>{stockLabel}</span>
                      ) : null}
                    </span>
                    {size.price != null ? (
                      <span className="shrink-0 font-semibold tabnum" style={{ color: NAVY }}>
                        {usd(size.price)}
                      </span>
                    ) : null}
                  </>
                );
                if (size.url) {
                  return (
                    <li key={size.id}>
                      <a
                        href={size.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(rowClass, "no-underline")}
                        data-testid={`catalog-size-${size.id}`}
                      >
                        {content}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={size.id}>
                    <div className={rowClass} data-testid={`catalog-size-${size.id}`}>
                      {content}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function MatchHeader({ product, verdict, panelOpen, setPanelOpen, bundle = null }) {
  const w = product?.walmart || {};
  const source = product?.source || {};

  // Spec paths with fallbacks for current /analyze/product fixture shape.
  const images = useMemo(
    () => normalizeImages(
      product?.images
      ?? w.images
      ?? (w.image ? [w.image] : null)
      ?? (source.image ? [source.image] : null)
      ?? [],
    ),
    [product?.images, w.images, w.image, source.image],
  );

  const upc = stripFixture(product?.upc ?? w.upc ?? source.upc ?? "");
  const rawId = product?.id;
  const isInternalId = rawId != null && String(rawId).startsWith("prod_");
  const itemId = stripFixture(
    (!isInternalId && rawId)
      || w.item_id_clean
      || w.walmart_item_id
      || rawId
      || "",
  );

  const title = stripFixture(w.title || source.title || "Untitled product");
  const brand = stripFixture(w.brand || source.brand || "");
  const price = w.price ?? source.price ?? null;
  const ratingRaw = w.rating ?? source.rating ?? product?.rating ?? null;
  const rating = (() => {
    const n = Number(ratingRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const reviewsRaw = w.reviews ?? source.reviews ?? product?.reviews ?? product?.num_reviews ?? null;
  const reviews = (() => {
    const n = Number(reviewsRaw);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  })();

  const crumbs = useMemo(() => {
    if (Array.isArray(product?.categoryPath) && product.categoryPath.length) {
      return product.categoryPath
        .map((c) => stripFixture(String(c ?? "")))
        .filter(Boolean);
    }
    const raw = product?.category ?? w.category ?? source.category ?? "";
    const cleaned = stripFixture(raw);
    if (!cleaned) return [];
    return cleaned.split(/[>›]/).map((s) => s.trim()).filter(Boolean);
  }, [product?.categoryPath, product?.category, w.category, source.category]);

  const buyBoxRaw = product?.competition?.buy_box_owner
    || product?.competition?.sold_and_shipped_by
    || (product?.competition?.walmart_presence ? "Walmart.com" : null)
    || null;
  const buyBox = buyBoxRaw ? stripFixture(buyBoxRaw) : null;

  // Never invent "In stock" from a bare competition object when availability is null.
  const avail = availabilityDisplay(product?.availability_label || product?.availability);
  const availability = avail?.label || null;
  const inStock = avail?.inStock === true;

  const badges = product?.badges || {};
  const purchaseBadge = normalizePurchaseActivityBadge(
    badges.purchase_activity || w.purchase_activity_badge || product?.purchase_activity_badge || "",
  );
  const merchBadge = (badges.merchandising || w.merchandising_badge || "").trim() || null;
  const keyFeatures = useMemo(
    () => (Array.isArray(product?.key_features) ? product.key_features.filter(Boolean) : []),
    [product?.key_features],
  );
  const rawDescription = w.description || source.description || product?.description || "";
  const { description: normalizedDescription, features: normalizedFeatures } = useMemo(
    () => normalizeProductDescription(rawDescription, keyFeatures),
    [rawDescription, keyFeatures],
  );
  const hasDescription = normalizedDescription.length > 0 || normalizedFeatures.length > 0;

  const catalogSizes = useMemo(
    () => collectCatalogSizes({
      variations: bundle?.variations || product?.variations || [],
      productDetails: bundle?.product_details || [],
      currentItemId: itemId,
    }),
    [bundle?.variations, bundle?.product_details, product?.variations, itemId],
  );
  const hasCatalogSizes = catalogSizes.length > 0;

  const collectedAt = w.collected_at || source.collected_at || null;
  const lastChecked = collectedAt ? ago(collectedAt) : null;
  const isStale = (() => {
    if (!collectedAt) return false;
    const t = new Date(collectedAt).getTime();
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) > 7 * 24 * 60 * 60 * 1000;
  })();

  const titleForSearch = stripFixture(w.title || source.title || "") || null;
  const wUrl = w.url || buildWalmartProductUrl(itemId || w.item_id_clean || w.walmart_item_id);

  const [sourceActive, setSourceActive] = useState("walmart");
  const [descOpen, setDescOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const sourceBtnRefs = useRef({});
  const detailsBtnRef = useRef(null);
  const catalogBtnRef = useRef(null);

  // Close popovers when navigating to / loading another product.
  useEffect(() => {
    setDescOpen(false);
    setCatalogOpen(false);
  }, [itemId, product?.id, title]);

  const closeDescription = useCallback(({ returnFocus = false } = {}) => {
    setDescOpen(false);
    if (returnFocus) {
      queueMicrotask(() => {
        detailsBtnRef.current?.focus?.();
      });
    }
  }, []);

  const closeCatalog = useCallback(({ returnFocus = false } = {}) => {
    setCatalogOpen(false);
    if (returnFocus) {
      queueMicrotask(() => {
        catalogBtnRef.current?.focus?.();
      });
    }
  }, []);

  const toggleDescription = useCallback(() => {
    setCatalogOpen(false);
    setDescOpen((open) => !open);
  }, []);

  const toggleCatalog = useCallback(() => {
    setDescOpen(false);
    setCatalogOpen((open) => !open);
  }, []);

  const sourceActions = useMemo(() => {
    const links = buildTitleSourceLinkActions({
      title: titleForSearch,
      walmartItemId: itemId,
      walmartUrl: wUrl,
    });
    return {
      ...links,
      catalog: {
        enabled: hasCatalogSizes,
        onClick: toggleCatalog,
        ariaExpanded: catalogOpen,
        ariaControls: CATALOG_POPOVER_ID,
        testId: "catalog-sizes-trigger",
      },
      details: {
        enabled: hasDescription,
        onClick: toggleDescription,
        ariaExpanded: descOpen,
        ariaControls: DESCRIPTION_POPOVER_ID,
        testId: "product-description-trigger",
      },
    };
  }, [
    titleForSearch,
    itemId,
    wUrl,
    hasCatalogSizes,
    toggleCatalog,
    catalogOpen,
    hasDescription,
    toggleDescription,
    descOpen,
  ]);

  return (
    <Panel
      className="p-5 min-w-0 max-[1024px]:p-4 max-[640px]:p-3 bg-white border rounded-2xl max-[640px]:rounded-xl shadow-[0_8px_28px_#183b5610] w-full overflow-hidden"
      style={{ borderColor: LINE }}
      testid="match-header"
    >
      {crumbs.length > 0 && (
        <nav
          className="flex items-center gap-1.5 text-[11px] sm:text-xs mb-2.5 sm:mb-3.5 min-w-0 overflow-hidden whitespace-nowrap"
          style={{ color: MUTED }}
          aria-label="Product category"
        >
          {crumbs.map((c, i) => (
            <React.Fragment key={`${c}-${i}`}>
              {i > 0 && (
                <i className="not-italic shrink-0" style={{ color: "#aebed0" }} aria-hidden="true">›</i>
              )}
              <span
                className={cn("min-w-0 overflow-hidden text-ellipsis", i === crumbs.length - 1 && "font-semibold")}
                style={i === crumbs.length - 1 ? { color: NAVY } : undefined}
              >
                {c}
              </span>
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Desktop: 143px gallery + info. Tablet: compact side-by-side. Keep image beside title through phone. */}
      <div className="grid grid-cols-[143px_minmax(0,1fr)] max-[920px]:grid-cols-[112px_minmax(0,1fr)] max-[560px]:grid-cols-[88px_minmax(0,1fr)] gap-[22px] max-[920px]:gap-3.5 max-[560px]:gap-2.5 items-start">
        <ProductMediaGallery images={images} title={title} showThumbnails={false} />

        <div className="min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {brand && (
                <div
                  className="text-[12px] sm:text-[13px] font-semibold mb-1.5 line-clamp-1"
                  style={{ color: MUTED }}
                  data-testid="product-brand"
                >
                  {brand}
                </div>
              )}

              {(merchBadge || purchaseBadge) && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="product-badges">
                  {merchBadge && (
                    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                      {merchBadge}
                    </span>
                  )}
                  {purchaseBadge && (
                    <span className="inline-flex items-center rounded-full border border-edge-subtle bg-[#fbfdff] px-2 py-0.5 text-[11px] font-semibold" style={{ color: NAVY }}>
                      {purchaseBadge}
                    </span>
                  )}
                </div>
              )}

              <h2
                className="text-[18px] sm:text-[20px] font-bold leading-[1.22] m-0 mb-1.5 sm:mb-2 max-w-[600px] line-clamp-2 max-[560px]:text-[16px] max-[560px]:leading-snug"
                style={{ color: NAVY }}
                title={title}
                data-testid="product-title"
              >
                {title}
              </h2>

              {(rating != null || reviews != null) && (
                <div
                  className="flex flex-wrap items-center gap-1.5 mb-2 text-[13px] sm:text-[14px]"
                  style={{ color: NAVY }}
                  data-testid="product-rating-row"
                  role="img"
                  aria-label={
                    rating != null && reviews != null
                      ? `Rated ${Number(rating).toFixed(1)} out of 5 from ${num(reviews)} reviews`
                      : rating != null
                        ? `Rated ${Number(rating).toFixed(1)} out of 5`
                        : `${num(reviews)} reviews`
                  }
                >
                  {rating != null && (
                    <>
                      <span className="inline-flex items-center gap-0.5 leading-none" aria-hidden="true" data-testid="product-stars">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span key={i} style={{ color: "var(--walmart-yellow)", fontSize: 13 }}>★</span>
                        ))}
                      </span>
                      <span className="font-semibold tabnum" data-testid="product-rating-value" aria-hidden="true">
                        {Number(rating).toFixed(1)}
                      </span>
                    </>
                  )}
                  {reviews != null && (
                    <span className="font-medium" style={{ color: MUTED }} data-testid="product-review-count" aria-hidden="true">
                      ({num(reviews)} reviews)
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {/* Never show unexplained INCOMPLETE engineering status in the product header. */}
              {verdict && verdict.verdict !== "INCOMPLETE" && (
                <VerdictBadge verdict={verdict.verdict} />
              )}
              {typeof setPanelOpen === "function" && (
                <button
                  type="button"
                  onClick={() => setPanelOpen((o) => !o)}
                  data-testid="toggle-decision-panel"
                  className="hidden lg:grid h-8 w-8 place-items-center rounded-lg border hover:bg-surface-2"
                  style={{ borderColor: LINE, color: NAVY }}
                  aria-label="Toggle decision panel"
                >
                  {panelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>

          <p className="text-[18px] sm:text-[21px] font-extrabold m-0 mb-2 sm:mb-2.5 max-[560px]:mb-1.5" style={{ color: NAVY }} data-testid="product-price">
            {price == null ? "—" : usd(price)}{" "}
            <small className="text-[11px] sm:text-xs font-semibold block sm:inline mt-0.5 sm:mt-0" style={{ color: MUTED }}>
              Walmart selling price
            </small>
          </p>

          {/* IDs + source: wrap cleanly; source bar full-width scroll on narrow widths */}
          <div className="flex flex-col gap-2 min-[720px]:flex-row min-[720px]:flex-wrap min-[720px]:items-center">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
              <IdChip label="UPC" value={upc || null} copyTitle="Copy UPC" />
              <IdChip label="Walmart Item ID" value={itemId || null} copyTitle="Copy Walmart Item ID" />
            </div>
            <ProductSourceIconBar
              active={catalogOpen ? "catalog" : descOpen ? "details" : sourceActive}
              onSelect={(id) => {
                if (id !== "details" && id !== "catalog") setSourceActive(id);
              }}
              actions={sourceActions}
              buttonRefs={sourceBtnRefs}
              detailsRef={detailsBtnRef}
              catalogRef={catalogBtnRef}
              className="w-full sm:w-auto"
            />
          </div>

          <ProductDescriptionPopover
            open={descOpen}
            onClose={closeDescription}
            anchorRef={detailsBtnRef}
            description={normalizedDescription}
            features={normalizedFeatures}
          />

          <CatalogSizesPopover
            open={catalogOpen}
            onClose={closeCatalog}
            anchorRef={catalogBtnRef}
            sizes={catalogSizes}
          />

          {(buyBox || availability || lastChecked) && (
            <div
              className="mt-2.5 sm:mt-3 border-t pt-2.5 sm:pt-3 flex flex-wrap gap-1.5 sm:gap-2"
              style={{ borderColor: LINE }}
            >
              {buyBox && (
                <span
                  className="inline-flex items-baseline gap-1.5 rounded-full border px-2 py-0.5 sm:px-2.5 sm:py-1 text-[11px] sm:text-xs font-semibold whitespace-nowrap leading-snug bg-[#fbfdff]"
                  style={{ borderColor: LINE, color: NAVY }}
                >
                  <b className="text-[10px] sm:text-[11px] font-semibold" style={{ color: MUTED }}>Buy box</b>
                  {buyBox}
                </span>
              )}
              {availability && (
                <span
                  className={cn(
                    "inline-flex items-baseline gap-1.5 rounded-full border px-2 py-0.5 sm:px-2.5 sm:py-1 text-[11px] sm:text-xs font-semibold whitespace-nowrap leading-snug",
                    inStock
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "bg-[#fbfdff]",
                  )}
                  style={inStock ? undefined : { borderColor: LINE, color: NAVY }}
                >
                  <b className={cn("text-[10px] sm:text-[11px] font-semibold", inStock ? "text-emerald-700/80" : "")} style={inStock ? undefined : { color: MUTED }}>
                    Availability
                  </b>
                  {availability}
                </span>
              )}
              {lastChecked && (
                <span
                  className={cn(
                    "inline-flex items-baseline gap-1.5 rounded-full border px-2 py-0.5 sm:px-2.5 sm:py-1 text-[11px] sm:text-xs font-semibold whitespace-nowrap leading-snug",
                    isStale
                      ? "border-amber-200 bg-amber-50 text-amber-900"
                      : "bg-[#fbfdff]",
                  )}
                  style={isStale ? undefined : { borderColor: LINE, color: NAVY }}
                >
                  <b className={cn("text-[10px] sm:text-[11px] font-semibold", isStale ? "text-amber-700/80" : "")} style={isStale ? undefined : { color: MUTED }}>
                    Last checked
                  </b>
                  {lastChecked}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

export default MatchHeader;
